import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { switchMap } from 'rxjs';

import type { ShareOnTheGoI18n } from '@core/access-to-candidate-legacy';
import { SHARE_ON_THE_GO_I18N, ShareOnTheGoService } from '@core/access-to-candidate-legacy';
import type { User } from '@core/account-types';
import type { FullCandidate } from '@core/candidate-types';
import { filterBoolean } from '@core/common-legacy';
import {
  FeatureGuardFacade,
  FeatureGuardMode,
  FeaturePlanIconColorDirective,
  GuardedFeature,
} from '@core/feature-guard-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { OfferSelectOptionHelper } from '@core/offers-ui';
import type { Placement } from '@core/placements-types';
import { EvaluationsAnalytics } from '@core/temp-legacy';
import {
  ButtonComponent,
  ConnectedOverlayDirective,
  IconComponent,
  OverlayOriginDirective,
  RtTooltip,
} from '@core/user-interface-legacy';
import { RequestEvaluationService } from '@recruitee/evaluations-logic';
import type { RequestEvaluationSubmit } from '@recruitee/evaluations-types';

import { RequestEvaluationDropdownComponent } from '../../dropdowns/request-evaluation-dropdown/request-evaluation-dropdown.component';

@Component({
  selector: 'rt-request-evaluation-container',
  templateUrl: './request-evaluation-container.component.html',
  styleUrls: ['./request-evaluation-container.component.less'],
  imports: [
    ButtonComponent,
    IconComponent,
    TranslatePipe,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    FeaturePlanIconColorDirective,
    RequestEvaluationDropdownComponent,
    RtTooltip,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    EvaluationsAnalytics,
    {
      provide: SHARE_ON_THE_GO_I18N,
      useValue: <ShareOnTheGoI18n>{
        submitBtn: 'app.candidate.share_on_the_go.allow_access_to_candidate.request_evaluation',
        dontRequestOption:
          'app.candidate.share_on_the_go.allow_access_to_candidate.dont_request_evaluation_from',
        dontRequestOptionTooltip:
          'app.candidate.share_on_the_go.allow_access_to_candidate.skip_user_tooltip',
      },
    },
  ],
})
export class RequestEvaluationContainerComponent {
  private readonly evaluationsAnalytics = inject(EvaluationsAnalytics);
  private readonly shareOnTheGoService = inject(ShareOnTheGoService);
  private readonly requestEvaluationService = inject(RequestEvaluationService);
  private readonly featureGuardFacade = inject(FeatureGuardFacade);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly GuardedFeature = GuardedFeature;

  protected readonly candidate = input<FullCandidate>();
  protected readonly availablePlacements = input<Placement[]>([]);
  protected readonly iconOnly = input(false);

  protected readonly isEvaluationFormsEntitled = toSignal(
    this.featureGuardFacade.checkFeatureAvailability(GuardedFeature.EvaluationForms),
    { initialValue: true },
  );

  protected readonly offers = computed(() =>
    OfferSelectOptionHelper.placementsToOfferSelectOptions(this.availablePlacements()),
  );

  protected readonly positions: ConnectionPositionPair[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  ];

  protected readonly evaluationDropdownOverlay = viewChild<ConnectedOverlayDirective>(
    'requestEvaluationDropdown',
  );

  protected onTriggerClick(overlay: ConnectedOverlayDirective): void {
    if (this.isEvaluationFormsEntitled()) {
      overlay.open = true;
    } else {
      this.featureGuardFacade.showFeatureInfo(
        FeatureGuardMode.Dialog,
        GuardedFeature.EvaluationForms,
      );
    }
  }

  protected onSubmit(data: RequestEvaluationSubmit): void {
    this.evaluationsAnalytics.trackRequestEvaluation();
    this.addInterviewRequest(data);
  }

  private addInterviewRequest(data: RequestEvaluationSubmit): void {
    const candidateId = this.candidate()!.id;

    this.shareOnTheGoService
      .openShareOnTheGoDialogIfPossible({
        shareOnTheGoDialogData: {
          offerId: data.offerId,
          talentPoolId: data.talentPoolId,
          candidateId,
          usersToShareWith: <User[]>data.admins,
          disableSubmitOnSkipAll: true,
        },
        viewContainerRef: this.evaluationDropdownOverlay()!.viewContainerRef,
      })
      .pipe(
        filterBoolean,
        switchMap(shareOnTheGoResult =>
          this.shareOnTheGoService.resultToRequestPayload({
            shareOnTheGoResult,
            offerId: data.offerId,
            talentPoolId: data.talentPoolId,
            candidateId,
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(shareOnTheGoRequestPayload => {
        this.evaluationDropdownOverlay()?.toggleOverlay(false);
        this.requestEvaluationService.createInterviewRequest({
          candidate: this.candidate()!,
          templateId: data.templateId,
          offerId: data.offerId,
          talentPoolId: data.talentPoolId,
          admins: <User[]>data.admins,
          dueDate: data.dueDate,
          shareOnTheGoRequestPayload,
        });
      });
  }
}
